import { getAccessToken, getRefreshToken, logout, refreshTokens } from './authService';
import { API_KEY, CLIENT_APP, CLIENT_NAME } from './clientConfig';

const API_BASE = 'https://dev.oliveeb.com';
const IDENTITY_API = `${API_BASE}/identity-service`;
const OLITASK_API = `${API_BASE}/olitask-service/api/v1`;

interface ApiOptions extends RequestInit {
  requiresAuth?: boolean;
  clientApp?: string;
}

const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {};
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return headers as Record<string, string>;
};

export async function apiFetch(
  endpoint: string,
  options: ApiOptions = {}
): Promise<Response> {
  const { requiresAuth = true, clientApp = CLIENT_APP, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'O-CLIENT-APP': clientApp,
    'O-API-KEY': API_KEY,
    'O-CLIENT-NAME': CLIENT_NAME,
    ...normalizeHeaders(fetchOptions.headers),
  };

  let url = endpoint;
  if (!endpoint.startsWith('http')) {
    const service = endpoint.includes('/auth/') ? IDENTITY_API : OLITASK_API;
    url = `${service}${endpoint}`;
  }

  if (requiresAuth) {
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    }
  }

  const isClientRegistrationRequest =
    url.includes('/clients/register/request') || url.includes('/clients/register/confirm');
  if (!isClientRegistrationRequest) {
    const clientId = await getClientId();
    if (clientId) {
      headers['O-CLIENT-ID'] = clientId;
    }
  }

  const makeRequest = async (token?: string | null): Promise<Response> => {
    const requestHeaders: Record<string, string> = { ...headers };
    if (token) {
      requestHeaders['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    }
    return fetch(url, {
      ...fetchOptions,
      headers: requestHeaders,
    });
  };

  let response = await makeRequest(getAccessToken());

  // If unauthorized and we have a refresh token, try to refresh
  if (response.status === 401 && getRefreshToken()) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      response = await makeRequest(getAccessToken());
    } else {
      await logout();
      window.location.href = '/signin';
    }
  }

  return response;
}

export async function apiGet<T>(endpoint: string, options?: ApiOptions): Promise<T> {
  const response = await apiFetch(endpoint, { method: 'GET', ...options });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `API request failed: ${response.status}`);
  }
  return response.json();
}

export async function apiPost<T>(
  endpoint: string,
  data?: any,
  options?: ApiOptions
): Promise<T> {
  const response = await apiFetch(endpoint, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
    ...options,
  });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `API request failed: ${response.status}`);
  }
  return response.json();
}

export async function apiPut<T>(
  endpoint: string,
  data?: any,
  options?: ApiOptions
): Promise<T> {
  const response = await apiFetch(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
    ...options,
  });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `API request failed: ${response.status}`);
  }
  return response.json();
}

export async function apiPatch<T>(
  endpoint: string,
  data?: any,
  options?: ApiOptions
): Promise<T> {
  const response = await apiFetch(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
    ...options,
  });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `API request failed: ${response.status}`);
  }
  return response.json();
}

export async function apiDelete<T>(endpoint: string, options?: ApiOptions): Promise<T> {
  const response = await apiFetch(endpoint, { method: 'DELETE', ...options });
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `API request failed: ${response.status}`);
  }
  return response.json();
}

export async function getClientId(): Promise<string | null> {
  // FIX: use localStorage (authService now uses localStorage, not sessionStorage)
  let clientId = localStorage.getItem('olitask_client_id');
  if (clientId && clientId !== 'undefined' && clientId !== 'null') {
    return clientId;
  }

  try {
    const response = await fetch(`${API_BASE}/api/v1/clients/register/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'O-API-KEY': API_KEY,
        'O-CLIENT-APP': CLIENT_APP,
      },
    });

    if (response.ok) {
      const data = await response.json();
      clientId = String(data.id || '');
      localStorage.setItem('olitask_client_id', clientId);
      return clientId;
    }
  } catch (error) {
    console.error('Failed to get clientId:', error);
  }
  return null;
}

export function getDeviceId(): string {
  let deviceId = localStorage.getItem('websiteDeviceId');
  if (!deviceId) {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      deviceId = 'web-' + crypto.randomUUID();
    } else {
      deviceId = 'web-' + Date.now() + '-' + Math.random().toString(36).substring(2, 15);
    }
    localStorage.setItem('websiteDeviceId', deviceId);
  }
  return deviceId;
}
